import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Search, Edit2, Trash2, Eye, X, Building2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const ESTADOS = ['prospecto', 'activo', 'inactivo'];
const SECTORES = ['Industrial', 'Petroquímica', 'Construcción', 'Minería', 'Naviero', 'Alimentos', 'Agua', 'Energía', 'Otro'];

const badgeEstado = (e) => {
  const map = { prospecto: 'badge-yellow', activo: 'badge-green', inactivo: 'badge-gray' };
  return <span className={`badge ${map[e] || 'badge-gray'}`}>{e}</span>;
};

//
const emptyForm = {
  empresa: '', tipoCliente: 'nacional', ruc: '', sector: '', ciudad: '', direccion: '',
  contactoNombre: '', contactoCargo: '', contactoEmail: '',
  contactoTelefono: '', estado: 'prospecto', notas: '', linkCarpeta: ''
};

// ── Validaciones ──────────────────────────────────────────────────────────────
// Los clientes extranjeros no manejan el RUC ecuatoriano de 13 dígitos — pueden
// tener letras y números de cualquier longitud, así que no se valida el formato.
const validarRUC = (ruc, tipoCliente) => {
  if (!ruc) return null; // optional field
  if (tipoCliente === 'extranjero') return null;
  const r = ruc.replace(/\s/g, '');
  if (!/^\d{13}$/.test(r)) return 'El RUC debe tener exactamente 13 dígitos numéricos';
  const provincia = parseInt(r.substring(0, 2));
  if (provincia < 1 || provincia > 24) return 'Los primeros 2 dígitos del RUC deben ser un código de provincia válido (01-24)';
  return null;
};

const validarTelefono = (tel) => {
  if (!tel) return null; // optional
  const t = tel.replace(/[\s\-\+]/g, '');
  if (!/^\d{10}$/.test(t)) return 'El teléfono debe tener 10 dígitos (ej: 0984563214)';
  return null;
};

const validarEmail = (email) => {
  if (!email) return null;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Correo electrónico inválido';
  return null;
};

export default function ClientesPage() {
  const { user } = useAuth();
  const puedeEliminar = ['admin', 'master'].includes(user?.rol);
  const isGerencia = ['admin', 'master', 'gerente_financiera', 'gerente_proyectos'].includes(user?.rol);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [todosUsuarios, setTodosUsuarios] = useState([]);
  const [orden, setOrden] = useState('reciente');
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errores, setErrores] = useState({});

  const load = async () => {
    try {
      const res = await api.get('/clients');
      setClientes(res.data);
    } catch { toast.error('Error al cargar clientes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!isGerencia) return;
    api.get('/users/all').then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      setTodosUsuarios(lista.filter(u => u.activo !== false));
    }).catch(() => {});
  }, [isGerencia]);

  const openCrear = () => { setForm(emptyForm); setErrores({}); setSelected(null); setModal('crear'); };
  const openEditar = (c) => { setForm({ ...c }); setErrores({}); setSelected(c); setModal('editar'); };
  const openVer = (c) => { setSelected(c); setModal('ver'); };
  const closeModal = () => { setModal(null); setSelected(null); setErrores({}); };

  const f = (k) => (e) => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, [k]: val }));
    // Validate on change
    if (k === 'ruc') setErrores(prev => ({ ...prev, ruc: validarRUC(val, form.tipoCliente) }));
    if (k === 'contactoTelefono') setErrores(prev => ({ ...prev, contactoTelefono: validarTelefono(val) }));
    if (k === 'contactoEmail') setErrores(prev => ({ ...prev, contactoEmail: validarEmail(val) }));
  };

  const setTipoCliente = (tipo) => {
    setForm(prev => ({ ...prev, tipoCliente: tipo }));
    setErrores(prev => ({ ...prev, ruc: validarRUC(form.ruc, tipo) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Run all validations before submitting
    const newErrors = {
      ruc: validarRUC(form.ruc, form.tipoCliente),
      contactoTelefono: validarTelefono(form.contactoTelefono),
      contactoEmail: validarEmail(form.contactoEmail),
    };
    setErrores(newErrors);

    if (Object.values(newErrors).some(v => v !== null)) {
      toast.error('Corrige los errores antes de guardar');
      return;
    }

    setSaving(true);
    try {
      if (modal === 'crear') {
        await api.post('/clients', form);
        toast.success('Cliente creado');
      } else {
        await api.put(`/clients/${selected.id}`, form);
        toast.success('Cliente actualizado');
      }
      await load();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`¿Eliminar a "${c.empresa}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success('Cliente eliminado');
      await load();
    } catch { toast.error('Error al eliminar'); }
  };

  const filtered = clientes.filter(c => {
    const matchSearch = !search ||
      c.empresa.toLowerCase().includes(search.toLowerCase()) ||
      (c.contactoNombre || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.ciudad || '').toLowerCase().includes(search.toLowerCase());
    const matchEstado = !filtroEstado || c.estado === filtroEstado;
    const matchUsuario = !filtroUsuario || c.userId === filtroUsuario;
    return matchSearch && matchEstado && matchUsuario;
  }).sort((a, b) => orden === 'alfabetico' ? a.empresa.trim().localeCompare(b.empresa.trim()) : 0);

  // ── Field with error display ──────────────────────────────────────────────
  const FieldError = ({ campo }) => errores[campo]
    ? <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
        ⚠ {errores[campo]}
      </div>
    : null;

  const inputStyle = (campo) => ({
    ...(errores[campo] ? { borderColor: '#dc2626', background: '#fff5f5' } : {})
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Clientes</h1>
          <p className="page-subtitle">{clientes.length} empresa{clientes.length !== 1 ? 's' : ''} registrada{clientes.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={openCrear}><Plus size={16} /> Nuevo cliente</button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <Search size={16} />
          <input className="form-input" placeholder="Buscar empresa, contacto, ciudad..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 38, width: 280 }} />
        </div>
        <select className="form-select" style={{ width: 160 }} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="form-select" style={{ width: 200 }} value={orden} onChange={e => setOrden(e.target.value)}>
          <option value="reciente">Más recientes primero</option>
          <option value="alfabetico">Ordenar alfabéticamente</option>
        </select>
        {isGerencia && todosUsuarios.length > 0 && (
          <select className="form-select" style={{ width: 200 }} value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)}>
            <option value="">Todos los asesores</option>
            {todosUsuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>)}
          </select>
        )}
        {(search || filtroEstado || filtroUsuario) && <button className="btn btn-secondary btn-sm" onClick={() => { setSearch(''); setFiltroEstado(''); setFiltroUsuario(''); }}>Limpiar</button>}
      </div>

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ width: 30, height: 30, margin: '0 auto' }} /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>Sin clientes</h3>
            <p>{search ? 'No se encontraron resultados' : 'Agrega tu primer cliente'}</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Contacto</th>
                  <th>Ciudad</th>
                  <th>Sector</th>
                  <th>Proyectos</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.empresa}</strong>
                      {c.tipoCliente === 'extranjero' && <span className="badge badge-blue" style={{ fontSize: 10, marginLeft: 6 }}>Extranjero</span>}
                      {c.ruc && <div style={{ fontSize: 12, color: '#888' }}>RUC: {c.ruc}</div>}
                    </td>
                    <td>
                      <div>{c.contactoNombre || '—'}</div>
                      {c.contactoEmail && <div style={{ fontSize: 12, color: '#888' }}>{c.contactoEmail}</div>}
                    </td>
                    <td>{c.ciudad || '—'}</td>
                    <td>{c.sector || '—'}</td>
                    <td><span className="badge badge-blue">{c.projects?.length || 0}</span></td>
                    <td>{badgeEstado(c.estado)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-icon" title="Ver" onClick={() => openVer(c)}><Eye size={15} /></button>
                        <button className="btn-icon" title="Editar" onClick={() => openEditar(c)}><Edit2 size={15} /></button>
                        {puedeEliminar && <button className="btn-icon" title="Eliminar" onClick={() => handleDelete(c)} style={{ color: '#dc2626' }}><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      {(modal === 'crear' || modal === 'editar') && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3 className="modal-title">{modal === 'crear' ? 'Nuevo cliente' : 'Editar cliente'}</h3>
              <button className="btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <h4 style={{ marginBottom: 14, color: '#7B0D0D', fontSize: 14, fontWeight: 600 }}>DATOS DE LA EMPRESA</h4>
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label className="form-label">Empresa *</label>
                    <input className="form-input" required value={form.empresa} onChange={f('empresa')} placeholder="Nombre de la empresa" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tipo de cliente</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button"
                        className={`btn btn-sm ${form.tipoCliente !== 'extranjero' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setTipoCliente('nacional')} style={{ flex: 1 }}>Nacional</button>
                      <button type="button"
                        className={`btn btn-sm ${form.tipoCliente === 'extranjero' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setTipoCliente('extranjero')} style={{ flex: 1 }}>Extranjero</button>
                    </div>
                  </div>
                </div>
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label className="form-label">
                      RUC {form.tipoCliente === 'extranjero'
                        ? <span style={{ color: '#888', fontWeight: 400, fontSize: 11 }}>(identificación fiscal extranjera, sin formato fijo)</span>
                        : <span style={{ color: '#888', fontWeight: 400, fontSize: 11 }}>(13 dígitos)</span>}
                    </label>
                    <input className="form-input" value={form.ruc} onChange={f('ruc')}
                      placeholder={form.tipoCliente === 'extranjero' ? 'Tax ID / N.° de registro' : '0000000000001'}
                      maxLength={form.tipoCliente === 'extranjero' ? undefined : 13} style={inputStyle('ruc')}/>
                    <FieldError campo="ruc"/>
                  </div>
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">Sector</label>
                    <select className="form-select" value={form.sector} onChange={f('sector')}>
                      <option value="">Seleccionar...</option>
                      {SECTORES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ciudad</label>
                    <input className="form-input" value={form.ciudad} onChange={f('ciudad')} placeholder="Guayaquil" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={form.estado} onChange={f('estado')}>
                      {ESTADOS.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Dirección</label>
                  <input className="form-input" value={form.direccion} onChange={f('direccion')} placeholder="Dirección completa" />
                </div>

                <h4 style={{ margin: '16px 0 14px', color: '#7B0D0D', fontSize: 14, fontWeight: 600 }}>CONTACTO PRINCIPAL</h4>
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label className="form-label">Nombre</label>
                    <input className="form-input" value={form.contactoNombre} onChange={f('contactoNombre')} placeholder="Nombre del contacto" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cargo</label>
                    <input className="form-input" value={form.contactoCargo} onChange={f('contactoCargo')} placeholder="Gerente de planta" />
                  </div>
                </div>
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label className="form-label">Correo</label>
                    <input className="form-input" type="email" value={form.contactoEmail} onChange={f('contactoEmail')}
                      placeholder="contacto@empresa.com" style={inputStyle('contactoEmail')}/>
                    <FieldError campo="contactoEmail"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Teléfono <span style={{ color: '#888', fontWeight: 400, fontSize: 11 }}>(10 dígitos)</span></label>
                    <input className="form-input" value={form.contactoTelefono} onChange={f('contactoTelefono')}
                      placeholder="0984563214" maxLength={10} style={inputStyle('contactoTelefono')}/>
                    <FieldError campo="contactoTelefono"/>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Link carpeta (Google Drive, Dropbox, etc.)</label>
                  <input className="form-input" type="url" value={form.linkCarpeta} onChange={f('linkCarpeta')} placeholder="https://drive.google.com/..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea className="form-textarea" value={form.notas} onChange={f('notas')} placeholder="Observaciones adicionales..." rows={3} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="spinner"/>Guardando...</> : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal ver */}
      {modal === 'ver' && selected && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3 className="modal-title">{selected.empresa}</h3>
              <button className="btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                {[
                  ['Tipo de cliente', selected.tipoCliente === 'extranjero' ? 'Extranjero' : 'Nacional'],
                  ['RUC', selected.ruc], ['Sector', selected.sector], ['Ciudad', selected.ciudad],
                  ['Estado', selected.estado], ['Dirección', selected.direccion],
                  ['Link carpeta', selected.linkCarpeta],
                ].map(([k, v]) => v && (
                  <div key={k} style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>
                      {k === 'Link carpeta'
                        ? <a href={v} target="_blank" rel="noopener noreferrer" style={{ color: '#7B0D0D' }}>Abrir carpeta</a>
                        : v}
                    </div>
                  </div>
                ))}
              </div>
              {selected.contactoNombre && (
                <div style={{ padding: 14, background: '#fff5f5', borderRadius: 8, border: '1px solid #fee2e2', marginBottom: 16 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: '#7B0D0D', marginBottom: 10 }}>CONTACTO PRINCIPAL</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[['Nombre', selected.contactoNombre], ['Cargo', selected.contactoCargo],
                      ['Correo', selected.contactoEmail], ['Teléfono', selected.contactoTelefono]
                    ].map(([k, v]) => v && (
                      <div key={k}><span style={{ fontSize: 12, color: '#888' }}>{k}: </span><span style={{ fontSize: 14 }}>{v}</span></div>
                    ))}
                  </div>
                </div>
              )}
              {selected.appointments?.length > 0 && (() => {
                const ubicaciones = [];
                const vistas = new Set();
                for (const a of selected.appointments) {
                  const key = `${a.empresaLugar || ''}|${a.direccion || ''}`;
                  if (vistas.has(key) || (!a.empresaLugar && !a.direccion)) continue;
                  vistas.add(key);
                  ubicaciones.push(a);
                }
                return ubicaciones.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 10 }}>HISTORIAL DE UBICACIONES ({ubicaciones.length})</h4>
                    {ubicaciones.map(a => (
                      <div key={a.id} style={{ padding: '8px 12px', background: '#fafafa', borderRadius: 6, marginBottom: 6 }}>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{a.empresaLugar || 'Sin nombre de lugar'}</div>
                        {a.direccion && <div style={{ fontSize: 13, color: '#666' }}>{a.direccion}</div>}
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
                          <span style={{ fontSize: 12, color: '#888' }}>Última cita ahí: {a.fecha}</span>
                          {a.linkMaps && <a href={a.linkMaps} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#7B0D0D' }}>Ver en mapa</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              {selected.notas && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>NOTAS</h4>
                  <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6 }}>{selected.notas}</p>
                </div>
              )}
              <div>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 10 }}>PROYECTOS ({selected.projects?.length || 0})</h4>
                {selected.projects?.length > 0 ? selected.projects.map(p => (
                  <div key={p.id} style={{ padding: '8px 12px', background: '#fafafa', borderRadius: 6, marginBottom: 6, fontSize: 14 }}>
                    {p.referencia} — <span className={`badge ${p.estado === 'finalizado' ? 'badge-green' : 'badge-blue'}`}>{p.estado}</span>
                  </div>
                )) : <p style={{ fontSize: 14, color: '#888' }}>Sin proyectos aún</p>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Cerrar</button>
              <button className="btn btn-primary" onClick={() => { closeModal(); setTimeout(() => openEditar(selected), 100); }}>Editar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
